<template>
  <div class="join-team-page overflow-x-hidden flex-shrink-0">
    <header-component logo="business" />
    <div class="container">
      <div class="flex flex-col m:flex-row justify-between">
        <div class="m:py-16 mb-8 m:w-1/2">
          <b-headline type="h1" typography="h2" class="mb-8">
            {{ $t("headline") }}
          </b-headline>
          <b-paragraph class="mb-8">
            {{ $t("subheadline") }}
          </b-paragraph>

          <div class="mb-8 flex flex-row gap-1 items-center">
            <arrow-back-icon class="w-6 h-6 transform text-blue mr-1" />
            <span class="text-blue font-bold">
              {{ $t("alreadyHaveAnAccount") }}
            </span>
            <a class="cursor-pointer font-bold underline" :href="loginPath">
              {{ $t("loginHere") }}
            </a>
          </div>

          <div class="m:w-8/12 flex flex-col mb-6">
            <signup-form
              form-id="join-team-page"
              legal-text-alignment="text-left"
              :simplified-form="true"
              :b2b-form="true"
              :initial-email="inviteeEmail"
            />
          </div>
        </div>
        <div
          class="hidden m:block py-16 w-1/2 text-center relative bg-cover bg-no-repeat bg-left join-team-page__image-lg"
        >
          <pict :default="imagePropsL.default" :alt="$t('alt')" block-name="join-team-page" class="w-full" />
        </div>
      </div>
    </div>
    <div class="m:hidden container bg-cover bg-no-repeat bg-top join-team-page__image-sm">
      <pict :default="imagePropsS.default" :alt="$t('alt')" block-name="join-team-page" class="w-full" />
    </div>
  </div>
</template>

<script>
import globals from "@/utils/globals"

import HeaderComponent from "@/features/header/components"
import SignupForm from "@/component-library/signup-form"
import BHeadline from "@/component-library/b-headline"
import BParagraph from "@/component-library/b-paragraph"
import Pict from "@/shared/pict"

import teamImageL from "@/pages/join-team-page/images/team-lg.png"
import teamImageL2x from "@/pages/join-team-page/images/team-lg@2x.png"
import teamImageS from "@/pages/join-team-page/images/team-sm.png"
import teamImageS2x from "@/pages/join-team-page/images/team-sm@2x.png"
import ArrowBackIcon from "@/component-library/icons/arrow-back-icon"
import { getSearchParam } from "@/utils/location"

export default {
  name: "JoinTeamPage",
  components: {
    HeaderComponent,
    SignupForm,
    BHeadline,
    BParagraph,
    ArrowBackIcon,
    Pict
  },
  props: {
    lastPageBeforeLogin: {
      type: String,
      required: false,
      default: null
    }
  },
  computed: {
    imagePropsL() {
      return {
        default: {
          src: teamImageL,
          srcset: {
            "2x": teamImageL2x
          }
        }
      }
    },
    imagePropsS() {
      return {
        default: {
          src: teamImageS,
          srcset: {
            "2x": teamImageS2x
          }
        }
      }
    },
    loginPath() {
      let url = globals.routes.login

      if (this.lastPageBeforeLogin) {
        url = `${url}?last_page_before_login=${encodeURIComponent(this.lastPageBeforeLogin)}`
      }

      return url
    },
    inviteeEmail() {
      return getSearchParam("email")
    }
  },
  i18n: {
    messages: {
      en: {
        headline: "Your organization has invited you to Blinkist!",
        subheadline:
          "Broaden your perspectives, find inspiration, be more innovative—with powerful ideas from over 7,500 nonfiction bestsellers and podcasts, right at your fingertips.",
        alreadyHaveAnAccount: "Already have an account?",
        loginHere: "Login here",
        alt: "A team sitting around a table"
      },
      de: {
        headline: "Dein Team hat dich zu Blinkist eingeladen!",
        subheadline:
          "Stärke deine Fähigkeiten und erfahre alles über die spannendsten Themen der Arbeitswelt – mit den besten Erkenntnissen aus mehr als 7.500 Sachbüchern.",
        alreadyHaveAnAccount: "Du hast bereits ein Konto?",
        loginHere: "Hier einloggen",
        alt: "Ein Team, das um einen Tisch versammelt sitzt."
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.join-team-page {
  &__image-lg {
    background-image: url("./images/background-lg.svg");

    &:after {
      content: "";

      position: absolute;
      top: 0;
      left: 100%;
      z-index: 0;

      display: block;
      width: 100vw;
      height: 100%;

      background-color: $background-blue;
    }
  }

  &__image-sm {
    background-image: url("./images/background-sm.svg");
  }

  ::v-deep &__image {
    width: 100%;
  }
}
</style>
